import { Button, Checkbox, IconButton, Input, Select, Switch, Textarea } from '@artosera/ui';
import { RUBRIQUES, THEMES, type Rubrique } from '@artosera/site-renderer';
import type { Langue } from '@artosera/shared';
import { ArrowDown, ArrowUp, Check, Plus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import {
  useModifierSite,
  type AdressePubliqueSite,
  type BlocAccueilSite,
  type EntreeMenuSite,
  type ReglagesSite,
  type TypeBlocAccueil,
} from '../../../api/site.js';
import { galerieALaction, galerieAModule } from '../../../auth/sujet.js';
import type { GalerieSession } from '../../../api/types.js';
import { Field } from '../../Field.js';
import { erreurEmail, erreurTelephone } from '../../validations.js';
import { VignetteTheme } from './themeVignettes.js';
import styles from './Tabs.module.css';

export interface ReglagesTabProps {
  galerieSlug: string;
  site: ReglagesSite;
  domaine: string;
  galerie: GalerieSession;
}

const LANGUES_SITE: readonly Langue[] = ['fr', 'en'];

const BLOCS_DISPONIBLES: readonly TypeBlocAccueil[] = [
  'boutique',
  'prise-rendez-vous',
  'exposition-en-cours',
  'oeuvres',
  'artistes',
  'agenda',
  'evenements',
  'actualites',
  'newsletter',
  'texte',
];

function blocParDefaut(type: TypeBlocAccueil): BlocAccueilSite {
  switch (type) {
    case 'boutique':
    case 'oeuvres':
      return { type, limite: 8 };
    case 'evenements':
    case 'agenda':
      return { type, limite: 4 };
    case 'actualites':
      return { type, limite: 3 };
    case 'texte':
      return { type, titre: { fr: '' }, corps: { fr: '' } };
    default:
      return { type };
  }
}

function deplacer<T>(liste: readonly T[], index: number, delta: number): T[] {
  const cible = index + delta;
  if (cible < 0 || cible >= liste.length) return [...liste];
  const copie = [...liste];
  const [element] = copie.splice(index, 1);
  if (element === undefined) return copie;
  copie.splice(cible, 0, element);
  return copie;
}

/**
 * Réglages (S6-f) : thème (vignette par thème), langues, menus, accueil par
 * blocs, coordonnées publiques, mention Artosera, domaine en lecture (§ 15.6).
 */
export function ReglagesTab({
  galerieSlug,
  site,
  domaine,
  galerie,
}: ReglagesTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'site', 'commun']);
  const modifier = useModifierSite(galerieSlug);
  const peutEcrire = galerieALaction(galerie, 'site:write');

  const [theme, setTheme] = useState(site.theme);
  const [langues, setLangues] = useState<Langue[]>([...site.langues]);
  const [langueParDefaut, setLangueParDefaut] = useState(site.langueParDefaut);
  const [menuPrincipal, setMenuPrincipal] = useState<Rubrique[]>(
    site.menus.principal.map((entree) => entree.rubrique),
  );
  const [menuPied, setMenuPied] = useState<Rubrique[]>(
    site.menus.pied.map((entree) => entree.rubrique),
  );
  const [accueil, setAccueil] = useState<BlocAccueilSite[]>([...site.accueil]);
  const [nouveauBloc, setNouveauBloc] = useState<TypeBlocAccueil>('texte');
  const [mentionArtosera, setMentionArtosera] = useState(site.mentionArtosera);
  // Coordonnées publiques : sans courriel ni adresse, le site ne se publie pas
  // (`site_incomplet`). On édite l'adresse PRINCIPALE ; les autres adresses
  // éventuelles (espaces secondaires, bureau) sont conservées telles quelles.
  const principale =
    site.identite.adresses.find((adresse) => adresse.usage === 'principale') ??
    site.identite.adresses[0];
  const [email, setEmail] = useState(site.identite.email);
  const [telephone, setTelephone] = useState(site.identite.telephone ?? '');
  const [rue, setRue] = useState(principale?.rue ?? '');
  const [codePostal, setCodePostal] = useState(principale?.codePostal ?? '');
  const [ville, setVille] = useState(principale?.ville ?? '');
  const [pays, setPays] = useState(principale?.pays ?? 'FR');
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState(false);

  function basculerMenu(
    liste: readonly Rubrique[],
    definir: (valeur: Rubrique[]) => void,
    rubrique: Rubrique,
  ): void {
    if (liste.includes(rubrique)) {
      definir(liste.filter((valeur) => valeur !== rubrique));
    } else {
      // L'ordre canonique de `RUBRIQUES` : pas de réordonnancement manuel des
      // menus dans cette version, seulement leur composition.
      definir(RUBRIQUES.filter((valeur) => liste.includes(valeur) || valeur === rubrique));
    }
  }

  function mettreAJourBloc(index: number, partiel: Partial<BlocAccueilSite>): void {
    setAccueil((precedent) =>
      precedent.map((bloc, i) =>
        i === index ? ({ ...bloc, ...partiel } as BlocAccueilSite) : bloc,
      ),
    );
  }

  function adressesAEnregistrer(): AdressePubliqueSite[] {
    const autres = site.identite.adresses.filter((adresse) => adresse !== principale);
    const complete = [rue, codePostal, ville, pays].every((valeur) => valeur.trim().length > 0);
    if (!complete) return [...autres];
    return [
      {
        ...(principale ?? {}),
        usage: principale?.usage ?? 'principale',
        rue: rue.trim(),
        codePostal: codePostal.trim(),
        ville: ville.trim(),
        pays: pays.trim().toUpperCase(),
      },
      ...autres,
    ];
  }

  const cleErreurEmail = erreurEmail(email);
  const cleErreurTelephone = erreurTelephone(telephone);

  async function enregistrer(): Promise<void> {
    setErreur(null);
    setSucces(false);
    if (cleErreurEmail || cleErreurTelephone) return;
    try {
      await modifier.mutateAsync({
        identite: {
          ...(email.trim().length > 0 ? { email: email.trim() } : {}),
          telephone: telephone.trim().length > 0 ? telephone.trim() : null,
          adresses: adressesAEnregistrer(),
        },
        theme,
        langues,
        langueParDefaut,
        menus: {
          principal: menuPrincipal.map((rubrique): EntreeMenuSite => ({ rubrique })),
          pied: menuPied.map((rubrique): EntreeMenuSite => ({ rubrique })),
        },
        accueil,
        mentionArtosera,
      });
      setSucces(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <div className={styles.formulaire}>
      <section className={styles.panneau} aria-labelledby="site-theme-titre">
        <div className={styles.panneauEntete}>
          <h2 id="site-theme-titre" className={styles.sectionTitre}>
            {t('regie:site.reglages.themeTitre')}
          </h2>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.themeGrille}>
            {THEMES.map((code) => (
              <button
                key={code}
                type="button"
                className={styles.themeCarte}
                data-selectionne={theme === code ? 'true' : 'false'}
                aria-pressed={theme === code}
                disabled={!peutEcrire}
                onClick={() => setTheme(code)}
              >
                <span className={styles.themeVignette}>
                  <VignetteTheme theme={code} titre={t(`site:themes.${code}`)} />
                  <span className={styles.themeCoche} aria-hidden="true">
                    <Check size={14} strokeWidth={2} />
                  </span>
                </span>
                <span className={styles.themeNom}>{t(`site:themes.${code}`)}</span>
                <span className={styles.themeDescription}>
                  {t(`regie:site.reglages.themes.${code}`)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.panneau} aria-labelledby="site-langues-titre">
        <div className={styles.panneauEntete}>
          <h2 id="site-langues-titre" className={styles.sectionTitre}>
            {t('regie:site.reglages.languesTitre')}
          </h2>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.grille2}>
            <div className={styles.listeCoches}>
              {LANGUES_SITE.map((code) => (
                <div key={code} className={styles.coche}>
                  <Checkbox
                    id={`site-langue-${code}`}
                    label={t(`commun:langues.${code}`)}
                    checked={langues.includes(code)}
                    disabled={!peutEcrire || (langues.length === 1 && langues.includes(code))}
                    onCheckedChange={(valeur) => {
                      setLangues((precedent) =>
                        valeur === true
                          ? [...precedent, code]
                          : precedent.filter((langue) => langue !== code),
                      );
                    }}
                  />
                </div>
              ))}
            </div>
            <Field label={t('regie:site.reglages.langueParDefaut')} htmlFor="site-langue-defaut">
              <Select
                id="site-langue-defaut"
                aria-label={t('regie:site.reglages.langueParDefaut')}
                value={langueParDefaut}
                onValueChange={(valeur) => setLangueParDefaut(valeur as Langue)}
                options={langues.map((code) => ({
                  value: code,
                  label: t(`commun:langues.${code}`),
                }))}
              />
            </Field>
          </div>
        </div>
      </section>

      <section className={styles.panneau} aria-labelledby="site-menus-titre">
        <div className={styles.panneauEntete}>
          <h2 id="site-menus-titre" className={styles.sectionTitre}>
            {t('regie:site.reglages.menusTitre')}
          </h2>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.grille2}>
            <div className={styles.cocheBoite}>
              <p className={styles.cocheLegende}>{t('regie:site.reglages.menuPrincipal')}</p>
              <div className={styles.listeCoches}>
                {RUBRIQUES.map((rubrique) => (
                  <div key={rubrique} className={styles.coche}>
                    <Checkbox
                      id={`menu-principal-${rubrique}`}
                      label={t(`regie:site.rubriques.${rubrique}`)}
                      checked={menuPrincipal.includes(rubrique)}
                      disabled={!peutEcrire}
                      onCheckedChange={() =>
                        basculerMenu(menuPrincipal, setMenuPrincipal, rubrique)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.cocheBoite}>
              <p className={styles.cocheLegende}>{t('regie:site.reglages.menuPied')}</p>
              <div className={styles.listeCoches}>
                {RUBRIQUES.map((rubrique) => (
                  <div key={rubrique} className={styles.coche}>
                    <Checkbox
                      id={`menu-pied-${rubrique}`}
                      label={t(`regie:site.rubriques.${rubrique}`)}
                      checked={menuPied.includes(rubrique)}
                      disabled={!peutEcrire}
                      onCheckedChange={() => basculerMenu(menuPied, setMenuPied, rubrique)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.panneau} aria-labelledby="site-accueil-titre">
        <div className={styles.panneauEntete}>
          <h2 id="site-accueil-titre" className={styles.sectionTitre}>
            {t('regie:site.reglages.accueilTitre')}
          </h2>
          <p className={styles.aide}>{t('regie:site.reglages.accueilAide')}</p>
        </div>
        <div className={styles.panneauCorps}>
          <ol className={styles.blocListe}>
            {accueil.map((bloc, index) => (
              <li key={`${bloc.type}-${String(index)}`} className={styles.blocCarte}>
                <div className={styles.blocEntete}>
                  <span className={styles.blocNumero} aria-hidden="true">
                    {index + 1}
                  </span>
                  <span className={styles.blocNom}>{t(`regie:site.blocs.${bloc.type}`)}</span>
                  {bloc.type === 'boutique' ||
                  bloc.type === 'oeuvres' ||
                  bloc.type === 'agenda' ||
                  bloc.type === 'evenements' ||
                  bloc.type === 'actualites' ? (
                    <div className={styles.blocLimite}>
                      <Field
                        label={t('regie:site.reglages.limiteChamp')}
                        htmlFor={`bloc-limite-${String(index)}`}
                      >
                        <Input
                          id={`bloc-limite-${String(index)}`}
                          type="number"
                          min={1}
                          max={bloc.type === 'evenements' ? 12 : 24}
                          mono
                          disabled={!peutEcrire}
                          value={bloc.limite ?? ''}
                          onChange={(event) => {
                            const limite = Number(event.target.value) || 1;
                            mettreAJourBloc(index, {
                              limite:
                                bloc.type === 'evenements'
                                  ? Math.max(1, Math.min(12, Math.trunc(limite)))
                                  : limite,
                            });
                          }}
                        />
                      </Field>
                    </div>
                  ) : null}
                  {peutEcrire ? (
                    <div className={styles.blocActions}>
                      <IconButton
                        variant="outline"
                        size="sm"
                        icon={<ArrowUp aria-hidden />}
                        aria-label={t('regie:site.reglages.deplacerHaut')}
                        onClick={() => setAccueil((precedent) => deplacer(precedent, index, -1))}
                        disabled={index === 0}
                      />
                      <IconButton
                        variant="outline"
                        size="sm"
                        icon={<ArrowDown aria-hidden />}
                        aria-label={t('regie:site.reglages.deplacerBas')}
                        onClick={() => setAccueil((precedent) => deplacer(precedent, index, 1))}
                        disabled={index === accueil.length - 1}
                      />
                      <IconButton
                        variant="outline"
                        size="sm"
                        icon={<Trash2 aria-hidden />}
                        aria-label={t('commun:actions.supprimer')}
                        onClick={() =>
                          setAccueil((precedent) => precedent.filter((_, i) => i !== index))
                        }
                      />
                    </div>
                  ) : null}
                </div>

                {bloc.type === 'texte' ? (
                  <div className={styles.grille2}>
                    <Field
                      label={t('regie:site.reglages.blocTitreFr')}
                      htmlFor={`bloc-titre-fr-${String(index)}`}
                    >
                      <Input
                        id={`bloc-titre-fr-${String(index)}`}
                        disabled={!peutEcrire}
                        value={bloc.titre?.fr ?? ''}
                        onChange={(event) =>
                          mettreAJourBloc(index, {
                            titre: { ...bloc.titre, fr: event.target.value },
                          })
                        }
                      />
                    </Field>
                    <Field
                      label={t('regie:site.reglages.blocTitreEn')}
                      htmlFor={`bloc-titre-en-${String(index)}`}
                    >
                      <Input
                        id={`bloc-titre-en-${String(index)}`}
                        disabled={!peutEcrire}
                        value={bloc.titre?.en ?? ''}
                        onChange={(event) =>
                          mettreAJourBloc(index, {
                            titre: { fr: bloc.titre?.fr ?? '', en: event.target.value },
                          })
                        }
                      />
                    </Field>
                    <Field
                      label={t('regie:site.reglages.blocCorpsFr')}
                      htmlFor={`bloc-corps-fr-${String(index)}`}
                    >
                      <Textarea
                        id={`bloc-corps-fr-${String(index)}`}
                        rows={3}
                        disabled={!peutEcrire}
                        value={bloc.corps?.fr ?? ''}
                        onChange={(event) =>
                          mettreAJourBloc(index, {
                            corps: { ...bloc.corps, fr: event.target.value },
                          })
                        }
                      />
                    </Field>
                    <Field
                      label={t('regie:site.reglages.blocCorpsEn')}
                      htmlFor={`bloc-corps-en-${String(index)}`}
                    >
                      <Textarea
                        id={`bloc-corps-en-${String(index)}`}
                        rows={3}
                        disabled={!peutEcrire}
                        value={bloc.corps?.en ?? ''}
                        onChange={(event) =>
                          mettreAJourBloc(index, {
                            corps: { fr: bloc.corps?.fr ?? '', en: event.target.value },
                          })
                        }
                      />
                    </Field>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>

          {peutEcrire ? (
            <div className={styles.ajoutBloc}>
              <Select
                aria-label={t('regie:site.reglages.ajouterBloc')}
                value={nouveauBloc}
                onValueChange={(valeur) => setNouveauBloc(valeur as TypeBlocAccueil)}
                options={BLOCS_DISPONIBLES.filter(
                  (code) =>
                    (code !== 'boutique' || galerieAModule(galerie, 'boutique')) &&
                    (code !== 'prise-rendez-vous' ||
                      galerieAModule(galerie, 'prise-rendez-vous')) &&
                    (code !== 'evenements' || galerieAModule(galerie, 'evenements')),
                ).map((code) => ({
                  value: code,
                  label: t(`regie:site.blocs.${code}`),
                }))}
              />
              <Button
                variant="secondary"
                onClick={() =>
                  setAccueil((precedent) => [...precedent, blocParDefaut(nouveauBloc)])
                }
              >
                <Plus size={16} strokeWidth={1.5} aria-hidden />
                {t('regie:site.reglages.ajouterBloc')}
              </Button>
            </div>
          ) : null}
        </div>
      </section>

      <section className={styles.panneau} aria-labelledby="site-coordonnees-titre">
        <div className={styles.panneauEntete}>
          <h2 id="site-coordonnees-titre" className={styles.sectionTitre}>
            {t('regie:site.reglages.coordonneesTitre')}
          </h2>
          <p className={styles.aide}>{t('regie:site.reglages.coordonneesAide')}</p>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.grilleAdresse}>
            <div className={styles.champMoitie}>
              <Field
                label={t('regie:site.reglages.emailChamp')}
                htmlFor="site-email"
                error={email.trim() && cleErreurEmail ? t(cleErreurEmail) : undefined}
              >
                <Input
                  id="site-email"
                  type="email"
                  disabled={!peutEcrire}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
            </div>
            <div className={styles.champMoitie}>
              <Field
                label={t('regie:site.reglages.telephoneChamp')}
                htmlFor="site-telephone"
                error={telephone.trim() && cleErreurTelephone ? t(cleErreurTelephone) : undefined}
              >
                <Input
                  id="site-telephone"
                  type="tel"
                  disabled={!peutEcrire}
                  value={telephone}
                  onChange={(event) => setTelephone(event.target.value)}
                />
              </Field>
            </div>
            <div className={styles.champPlein}>
              <Field label={t('regie:site.reglages.rueChamp')} htmlFor="site-rue">
                <Input
                  id="site-rue"
                  disabled={!peutEcrire}
                  value={rue}
                  onChange={(event) => setRue(event.target.value)}
                />
              </Field>
            </div>
            <div className={styles.champCodePostal}>
              <Field label={t('regie:site.reglages.codePostalChamp')} htmlFor="site-code-postal">
                <Input
                  id="site-code-postal"
                  disabled={!peutEcrire}
                  value={codePostal}
                  onChange={(event) => setCodePostal(event.target.value)}
                />
              </Field>
            </div>
            <div className={styles.champVille}>
              <Field label={t('regie:site.reglages.villeChamp')} htmlFor="site-ville">
                <Input
                  id="site-ville"
                  disabled={!peutEcrire}
                  value={ville}
                  onChange={(event) => setVille(event.target.value)}
                />
              </Field>
            </div>
            <div className={styles.champPays}>
              <Field label={t('regie:site.reglages.paysChamp')} htmlFor="site-pays">
                <Input
                  id="site-pays"
                  mono
                  maxLength={2}
                  disabled={!peutEcrire}
                  value={pays}
                  onChange={(event) => setPays(event.target.value)}
                />
              </Field>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.panneau} aria-labelledby="site-autres-titre">
        <div className={styles.panneauEntete}>
          <h2 id="site-autres-titre" className={styles.sectionTitre}>
            {t('regie:site.reglages.autresTitre')}
          </h2>
        </div>
        <div className={styles.panneauCorps}>
          <div className={styles.interrupteur}>
            <Switch
              id="site-mention"
              label={t('regie:site.reglages.mentionArtosera')}
              checked={mentionArtosera}
              disabled={!peutEcrire || !site.mentionDesactivable}
              onCheckedChange={(valeur) => setMentionArtosera(valeur)}
            />
            {!site.mentionDesactivable ? (
              <p className={styles.aide}>{t('regie:site.reglages.mentionVerrouillee')}</p>
            ) : null}
          </div>

          <div className={styles.domaine}>
            <Field label={t('regie:site.reglages.domaineChamp')} htmlFor="site-domaine">
              <Input id="site-domaine" mono disabled value={domaine} />
            </Field>
            <p className={styles.aide}>{t('regie:site.reglages.domaineAide')}</p>
          </div>
        </div>
      </section>

      {erreur || succes || peutEcrire ? (
        <div className={styles.barreActions}>
          <div className={styles.barreMessages}>
            {erreur ? (
              <p className={styles.erreur} role="alert">
                {erreur}
              </p>
            ) : null}
            {succes ? (
              <p className={styles.succes} role="status">
                {t('site:messages.enregistre')}
              </p>
            ) : null}
          </div>
          {peutEcrire ? (
            <Button onClick={() => void enregistrer()} loading={modifier.isPending}>
              {t('commun:actions.enregistrer')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
